// Module Aide : rubriques d'articles (recherche), FAQ et contact support.
// Les tutoriels vidéo seront ajoutés ultérieurement (placeholder).
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Search, LifeBuoy, ChevronDown, Video, Mail, Phone, Send, Loader2,
} from "lucide-react";

const SUPPORT_EMAIL = "contact@groupeaerhpolynesie.com";
const SUPPORT_TEL = "87 73 78 15";

// Contenu d'aide structuré par rubriques.
const RUBRIQUES = [
  {
    titre: "Démarrer avec Porinetia Compta",
    articles: [
      { t: "Comment créer mon compte", c: ["Rendez-vous sur la page d'inscription et renseignez votre email et un mot de passe sécurisé (au moins 8 caractères).", "Vous recevrez un accès immédiat à votre espace, puis vous serez invité à paramétrer votre entreprise."] },
      { t: "Comment paramétrer mon entreprise", c: ["Lors de votre première connexion, complétez le formulaire d'onboarding : raison sociale, numéro Tahiti, régime TVA, adresse, etc.", "Ces informations alimentent automatiquement vos factures, états et déclarations."], etapes: ["Saisissez votre raison sociale et votre numéro Tahiti", "Choisissez votre régime TVA (mensuel ou trimestriel)", "Renseignez votre adresse et vos coordonnées", "Validez : votre exercice comptable est créé automatiquement"] },
      { t: "Comment naviguer dans l'application", c: ["Le menu latéral donne accès à la saisie (factures, banque, caisse, opérations diverses), aux états comptables, aux rapprochements et aux paramètres.", "Le tableau de bord affiche en un coup d'œil votre trésorerie, votre résultat et votre TVA à reverser."] },
    ],
  },
  {
    titre: "Saisir mes opérations",
    articles: [
      { t: "Comment saisir une facture client", c: ["Menu Saisie → Facture client. Choisissez le client, ajoutez les lignes (description, montant HT, taux de TVA) et validez.", "La facture est automatiquement comptabilisée dans le journal des ventes."] },
      { t: "Comment saisir une facture fournisseur", c: ["Menu Saisie → Facture fournisseur. Sélectionnez le fournisseur et saisissez les lignes d'achat.", "La TVA déductible est calculée automatiquement et reprise dans votre déclaration."] },
      { t: "Comment enregistrer un mouvement banque", c: ["Menu Banque. Indiquez s'il s'agit d'une entrée ou d'une sortie, le montant, la date et la contrepartie."] },
      { t: "Comment enregistrer un mouvement caisse", c: ["Menu Caisse. Même principe que la banque, pour vos opérations en espèces."] },
      { t: "Comment faire mon arrêté de caisse", c: ["Menu Caisse → Arrêté de caisse. Comptez votre caisse physique et saisissez le solde réel.", "L'écart éventuel avec le solde théorique est enregistré. Un arrêté validé est définitif et ne peut plus être modifié."] },
      { t: "Comment saisir une OD salaires", c: ["Menu Opérations diverses → OD Salaires. Renseignez le salaire brut, les charges patronales et les cotisations salariales."] },
    ],
  },
  {
    titre: "Mes états comptables",
    articles: [
      { t: "Lire ma balance générale", c: ["La balance liste tous vos comptes avec leur solde débiteur ou créditeur. C'est la photographie de votre comptabilité à un instant donné."] },
      { t: "Lire mon grand livre", c: ["Le grand livre détaille toutes les écritures, compte par compte. Idéal pour retrouver le détail d'un mouvement."] },
      { t: "Lire mon compte de résultat", c: ["Le compte de résultat compare vos produits (recettes) et vos charges (dépenses) pour faire ressortir votre bénéfice ou votre perte."] },
      { t: "Lire mon bilan", c: ["Le bilan présente ce que votre entreprise possède (actif) et comment elle est financée (passif). Actif et passif s'équilibrent toujours."] },
      { t: "Faire ma déclaration TVA", c: ["Menu États → Déclaration TVA. Le formulaire DICP 1010 est pré-rempli depuis vos factures.", "Complétez les champs libres si nécessaire, puis validez : la déclaration est verrouillée et exportable en PDF."] },
    ],
  },
  {
    titre: "Rapprochements",
    articles: [
      { t: "Comment faire mon rapprochement bancaire", c: ["Menu Rapprochements. Choisissez le mois, saisissez le solde de votre relevé bancaire réel, puis cochez les écritures qui apparaissent sur le relevé.", "Lorsque l'écart est nul, validez : le rapprochement est verrouillé et les écritures pointées deviennent « rapprochées »."], etapes: ["Sélectionnez le mois à rapprocher", "Saisissez le solde de votre relevé", "Cochez les écritures concordantes", "Vérifiez que l'écart est à zéro", "Validez le rapprochement"] },
    ],
  },
  {
    titre: "Questions fréquentes",
    articles: [
      { t: "Mes données sont-elles sauvegardées ?", c: ["Oui. Toutes vos données sont enregistrées en continu et de façon sécurisée. Vous n'avez aucune sauvegarde manuelle à effectuer."] },
      { t: "Comment exporter mes données pour mon expert-comptable ?", c: ["Chaque état comptable (balance, grand livre, journaux, etc.) propose un export CSV et XLS, et la déclaration TVA un export PDF.", "Ces fichiers utilisent la terminologie comptable standard, prêts à transmettre à votre expert-comptable."] },
      { t: "Comment changer mon régime TVA ?", c: ["Le régime TVA est un paramètre sensible : il ne peut pas être modifié directement.", `Contactez le support AERH à ${SUPPORT_EMAIL} pour toute demande de modification.`] },
      { t: "Que faire en cas d'erreur de saisie ?", c: ["Tant que l'exercice n'est pas clôturé, vous pouvez créer une opération corrective.", "Les documents validés (arrêté de caisse, déclaration TVA, rapprochement) et les exercices clôturés sont définitifs. En cas de doute, contactez le support."] },
    ],
  },
];

export default function Aide() {
  const [recherche, setRecherche] = useState("");
  const [ouvert, setOuvert] = useState(null);
  const [contact, setContact] = useState({ nom: "", email: "", sujet: "", message: "" });
  const [envoi, setEnvoi] = useState(false);

  const rubriquesFiltrees = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    if (!q) return RUBRIQUES;
    return RUBRIQUES.map((r) => ({
      ...r,
      articles: r.articles.filter((a) => a.t.toLowerCase().includes(q) || a.c.join(" ").toLowerCase().includes(q)),
    })).filter((r) => r.articles.length > 0);
  }, [recherche]);

  const envoyer = async () => {
    if (!contact.nom || !contact.email || !contact.message) return toast.error("Merci de renseigner votre nom, email et message.");
    setEnvoi(true);
    try {
      const { data } = await api.post("/support/contact", contact);
      toast.success(data.message);
      setContact({ nom: "", email: "", sujet: "", message: "" });
    } catch {
      toast.error("L'envoi a échoué. Veuillez réessayer.");
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="fenua-fade-up max-w-4xl" data-testid="page-aide">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-lg bg-[#E8F4F4] flex items-center justify-center"><LifeBuoy className="h-6 w-6 text-[#1D3D3D]" /></div>
        <div>
          <h1 className="text-3xl font-black tracking-tight text-[#1D3D3D]">Aide</h1>
          <p className="text-sm text-[#1D3D3D]/70">Trouvez des réponses ou contactez notre équipe.</p>
        </div>
      </div>

      {/* Recherche */}
      <div className="mt-6 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4A8A8A]" />
        <Input data-testid="recherche-aide" value={recherche} onChange={(e) => setRecherche(e.target.value)}
          placeholder="Que recherchez-vous ?" className="pl-9 bg-white border-[#4A8A8A]/30 h-11" />
      </div>

      {/* Rubriques */}
      <div className="mt-6 space-y-6">
        {rubriquesFiltrees.length === 0 ? (
          <p data-testid="aucun-resultat" className="text-center text-sm text-[#1D3D3D]/60 py-6">Aucun article ne correspond à votre recherche.</p>
        ) : rubriquesFiltrees.map((r) => (
          <div key={r.titre} data-testid="rubrique">
            <h2 className="text-sm font-bold uppercase tracking-wide text-[#4A8A8A] mb-2">{r.titre}</h2>
            <div className="bg-white border border-[#4A8A8A]/20 rounded-lg divide-y divide-[#4A8A8A]/10 overflow-hidden">
              {r.articles.map((a) => {
                const id = a.t;
                const isOpen = ouvert === id;
                return (
                  <div key={id} data-testid="article">
                    <button onClick={() => setOuvert(isOpen ? null : id)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#E8F4F4]/40">
                      <span className="font-medium text-[#1D3D3D]">{a.t}</span>
                      <ChevronDown className={`h-4 w-4 text-[#4A8A8A] transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4 text-sm text-[#1D3D3D]/80 space-y-2">
                        {a.c.map((p, i) => <p key={i}>{p}</p>)}
                        {a.etapes && (
                          <ol className="list-decimal list-inside space-y-1 text-[#1D3D3D]/80">
                            {a.etapes.map((s, i) => <li key={i}>{s}</li>)}
                          </ol>
                        )}
                        <p className="flex items-center gap-1.5 text-xs text-[#4A8A8A] pt-1"><Video className="h-3.5 w-3.5" /> Tutoriel vidéo à venir</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Contact support */}
      <div data-testid="contact-support" className="mt-8 bg-[#1D3D3D] text-white rounded-lg p-6">
        <h2 className="text-lg font-bold">Vous n'avez pas trouvé votre réponse ?</h2>
        <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-white/80">
          <span className="flex items-center gap-1.5"><Mail className="h-4 w-4" /> {SUPPORT_EMAIL}</span>
          <span className="flex items-center gap-1.5"><Phone className="h-4 w-4" /> {SUPPORT_TEL}</span>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-white/80">Nom</Label>
            <Input data-testid="contact-nom" value={contact.nom} onChange={(e) => setContact((c) => ({ ...c, nom: e.target.value }))} className="bg-white text-[#1D3D3D] h-9" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-white/80">Email</Label>
            <Input data-testid="contact-email" type="email" value={contact.email} onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))} className="bg-white text-[#1D3D3D] h-9" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs font-semibold text-white/80">Sujet</Label>
            <Input data-testid="contact-sujet" value={contact.sujet} onChange={(e) => setContact((c) => ({ ...c, sujet: e.target.value }))} className="bg-white text-[#1D3D3D] h-9" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs font-semibold text-white/80">Message</Label>
            <Textarea data-testid="contact-message" value={contact.message} onChange={(e) => setContact((c) => ({ ...c, message: e.target.value }))} className="bg-white text-[#1D3D3D]" rows={4} />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button data-testid="envoyer-message" onClick={envoyer} disabled={envoi} className="bg-white text-[#1D3D3D] hover:bg-white/90">
            {envoi ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />} Envoyer un message
          </Button>
        </div>
      </div>
    </div>
  );
}
